import { Package } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { formatGiftValue } from "@/lib/format-gift-value"
import { cn } from "@/lib/utils"
import type { Locale } from "@/types"
import type {
  CollabOfferedProduct,
  CollabProduct,
  MissionType,
} from "@/types/collab"

/**
 * OKS-986 — the compact gift row on a browse card.
 *
 * On a drop mission the free product IS the incentive, so it is surfaced on
 * every browse surface BEFORE the creator commits: the missions home, the
 * drop reveal, and the welcome-back rejoin card. The full product display
 * (60px photo, caption, `Qty n · value about …`) stays on the mission detail —
 * `<ProductBlock>`.
 *
 * Anatomy (UX Spec D2.1/D3): a 48px tile beside a two-line column matching its
 * height — line 1 an uppercase eyebrow, line 2 the name (truncates) then the
 * value (never truncates) and the quantity when >1.
 *
 * ## Two constraints that are not stylistic
 *
 * 1. **This must stay STATIC FLOW CONTENT.** `MissionCard` navigates via a
 *    `z-0` stretched-link overlay and this row is its SIBLING, not its child.
 *    Adding `relative`/`absolute` here paints the row above the link and
 *    swallows taps on the most persuasive part of the card — the same trap the
 *    cover slot needed `pointer-events-none` for. If this ever needs to be
 *    positioned, it needs `pointer-events-none` with it.
 * 2. **It takes no CTA prop and knows nothing about the entry flow.** OKS-988
 *    reworks the drop CTA and its recency line on these same surfaces and must
 *    be able to render around this component without editing it.
 *
 * Absence is the contract: the server omits `collab.product` entirely for open
 * missions, productless drops, and drops that have not fired yet (the reveal is
 * withheld from the wire, not hidden in the DOM — `Collabs::CardSerializer`).
 * So there is no `locked` branch here and there must not be one.
 */
interface GiftLineProps {
  /** `collab.product` — absent/null on every non-gifting row. */
  product: CollabProduct | null | undefined
  /**
   * OKS-990 — `collab.offer`, present only on a multi-product drop (PRD R2).
   *
   * ⚠️ The three SLOTS are substituted, never restructured (UX §S1). A measured
   * geometry arm watches the truncation classes enumerated below, and the
   * multi-product string (« 3 produits au choix ») is SHORTER than the product
   * names it replaces — so a regression in those classes would not surface
   * visually on this branch. Change the contents, never the anatomy.
   */
  offer?: CollabOfferedProduct[] | null
  /** Drives gift-vs-product wording; a promotional item is BOUGHT, not gifted. */
  missionType: MissionType
  locale: Locale
  className?: string
}

export function GiftLine({
  product,
  offer,
  missionType,
  locale,
  className,
}: GiftLineProps) {
  const { t } = useTranslation()
  // Mirrors `MissionCard`'s shipped `coverErrored` pattern: a URL that fails
  // to load (rehost rot, signed-URL expiry, 404) degrades to the same tile the
  // never-had-a-photo case uses, rather than a broken-image glyph.
  const [photoErrored, setPhotoErrored] = useState(false)

  if (!product) return null

  const multi = (offer?.length ?? 0) >= 2
  const photoUrl = photoErrored ? null : product.photo_url
  // ⚠️ TWO facts, not one. `value` is what to render; `spread` is whether the
  // offered values DIFFER, which decides "up to {max}" vs the plain value
  // (PRD A2). Deriving the copy from `multi` alone renders "up to" on an offer
  // whose members all cost the same — technically true, and a hedge the brand
  // did not ask for. My own arm caught exactly that.
  const offerMoney = multi ? offerValue(offer!, locale) : null
  const value = multi
    ? (offerMoney?.text ?? null)
    : product.value_source === "none"
      ? null
      : formatGiftValue(
          product.effective_value_cents,
          product.effective_currency,
          locale,
        )
  // ⚠️ THREE wordings now, not two, and they are mutually exclusive:
  // `from` (the amount is a floor), `up_to` (exact values that differ), plain.
  // `from` is checked FIRST — see `offerValue` for why a floor can never be
  // rendered as a ceiling.
  const from = multi ? (offerMoney?.from ?? false) : product.value_is_range
  const spread = offerMoney?.spread ?? false
  // ⚠️ The per-product quantity chip is OMITTED on a multi-product offer.
  // Quantity is per product; a single number beside "3 products to choose from"
  // is a false statement about an offer the creator has not chosen from yet.
  const showQuantity = !multi && product.quantity > 1

  return (
    <div
      className={cn("flex items-center gap-2.5", className)}
      data-testid="mission-card-gift-line"
      data-mission-type={missionType}
    >
      <div
        className={cn(
          "flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg",
          !photoUrl && "bg-muted text-muted-foreground",
        )}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            /* The name sits beside it — announcing both double-reads (UX §4). */
            alt=""
            className="size-full object-cover"
            data-testid="mission-card-gift-photo"
            onError={() => setPhotoErrored(true)}
          />
        ) : (
          <Package
            className="size-5"
            aria-hidden="true"
            data-testid="mission-card-gift-fallback-tile"
          />
        )}
      </div>

      <div className="flex min-w-0 flex-col">
        {/*
          The gift is introduced by a WORD, never by the accent colour or the
          icon alone (UX §4 — never colour-only).
        */}
        <span
          className="text-muted-foreground text-[0.6875rem] font-semibold tracking-wide uppercase"
          data-testid="mission-card-gift-eyebrow"
        >
          {t(
            missionType === "promotional"
              ? "creator_home.gift.eyebrow_promotional"
              : "creator_home.gift.eyebrow_drop",
          )}
        </span>

        <span className="flex min-w-0 items-baseline gap-1.5">
          {/*
            UX D3 — at 402px the NAME absorbs the squeeze and the value
            survives. THREE classes carry that and all three are load-bearing;
            this row lost one of them twice during implementation, each time
            invisibly, so they are enumerated rather than summarised:

              - `min-w-0` on the row above, so the row can shrink inside its
                column;
              - `min-w-0` on the NAME below — `truncate` ALONE DOES NOTHING to
                a flex item, whose default `min-width: auto` refuses to shrink
                below its content. Without it the name overflows instead of
                ellipsing;
              - `shrink-0` on the value, so the squeeze lands on the name and
                not on the most persuasive token on the row.

            The full name is always available on the detail.
          */}
          <span
            className="min-w-0 truncate text-sm font-medium"
            data-testid="mission-card-gift-name"
          >
            {multi
              ? t("creator_home.gift.multi_name", { count: offer!.length })
              : product.name}
          </span>
          {value && (
            <span
              className="text-primary shrink-0 text-xs font-semibold"
              data-testid="mission-card-gift-value"
              /* OKS-1000 — which WORDING branch rendered, independent of
                 language. An assertion on the copy is really an assertion about
                 the locale the harness happens to run in; this names the branch
                 itself, which is what both Vitest and the Playwright arms
                 actually want to pin. */
              data-value-wording={from ? "from" : spread ? "up_to" : "plain"}
              /* Absent on a multi-product token, which describes a SET whose
                 members may resolve from different rungs — there is no single
                 source to name. */
              data-value-source={multi ? undefined : product.value_source}
            >
              {from
                ? t("creator_home.gift.value_from", { value })
                : spread
                  ? t("creator_home.gift.value_up_to", { value })
                  : t("creator_home.gift.value", { value })}
            </span>
          )}
          {showQuantity && (
            <span
              className="text-muted-foreground shrink-0 text-xs"
              data-testid="mission-card-gift-quantity"
            >
              {t("creator_home.gift.quantity", { count: product.quantity })}
            </span>
          )}
        </span>
      </div>
    </div>
  )
}

/**
 * OKS-990 / OKS-1000 — the single value token on a MULTI-product card.
 *
 * ⚠️ **The four cases are evaluated TOP-DOWN and the order is the correctness
 * rule, not a style.** An implementation that tests the range case first
 * renders « dès … » on an offer containing an unresolvable member — it passes
 * every fixture whose members all resolve, which is most of them, and is wrong
 * on exactly the offer that mixes sources.
 *
 *   1. any member `none`, OR currencies differ  ->  NOTHING
 *   2. any member is a FLOOR                    ->  « dès {min} »
 *   3. all exact, they differ                   ->  « jusqu'à {max} »
 *   4. all exact and equal                      ->  the value, plainly
 *
 * Case 1's two suppressions are the shipped ones and both are deliberate.
 * Stating one number for an offer whose members disagree about currency would
 * render the max in whichever code happened to come first — a number the brand
 * never offered. And a `none` member means we do not know what one of the
 * choices is worth, so no single token can honestly describe the set.
 *
 * ⚠️ Case 2 takes the MIN with "from", never the max with "up to". A ranged
 * member's amount is a LOWER bound, so a maximum computed over it is a ceiling
 * the offer does not actually have — a false statement, and the more
 * persuasive-sounding of the two, which is why it is named here rather than
 * left to the table.
 *
 * ⚠️ `spread` is computed on the CENTS, never on the formatted strings: two
 * amounts can format identically once rounded, and hedging over a difference
 * nobody can see is noise.
 */
function offerValue(
  offer: CollabOfferedProduct[],
  locale: Locale,
): { text: string; spread: boolean; from: boolean } | null {
  if (offer.length === 0) return null

  // Case 1a — an unresolvable member. Checked on the SOURCE, not on the
  // amount: `0` is a legal resolved value and is falsy.
  if (offer.some((p) => p.value_source === "none")) return null

  // Case 1b — mixed currencies.
  const currency = offer[0].effective_currency
  if (offer.some((p) => p.effective_currency !== currency)) return null

  // Non-null by case 1a, which returns early on every unresolved member.
  const cents = offer.map((p) => p.effective_value_cents!)
  const min = Math.min(...cents)
  const max = Math.max(...cents)

  // Case 2 — any floor in the set makes the whole token a floor.
  const from = offer.some((p) => p.value_is_range)
  const text = formatGiftValue(from ? min : max, currency, locale)
  if (text == null) return null

  return { text, spread: !from && min !== max, from }
}
